// src/types.ts
import type { ConnectionState, IntegrationState } from '@apideck/unify/models/components/index.js';
import type { LoginMethod, OrgMemberInfo } from '@propelauth/node';
import type { Request } from 'express';
import type { OrganisationGetOneResponseDto } from '../organisation/dto/organisation-get-one.dto.js';
import type { FactV2 } from '../matter/fact/fact.entity.js';
import type { FactSubsetRelevance } from '../matter/fact/fact-subset-relevance.entity.js';
import type { MetadataFieldFilter } from '../matter/subset/dto/metadata-filter.dto.js';

export interface User {
  userId: string;
  orgIdToOrgMemberInfo: {
    [key: string]: OrgMemberInfo;
  };
}

export interface CustomRequest extends Request {
  user?: User & { email: string; loginMethod: LoginMethod; orgId: string; userId: string };
}

/** Terminal state of an async export job, surfaced via /api/export-status. */
export enum ExportStatus {
  Completed = 'completed',
  Failed = 'failed',
}

/**
 * URL `exportType` query param values. Used by backend services when building
 * the post-export deep link and by the frontend `ExportReadyDialog` to pick
 * the matching handler. Keep in sync with the dialog's `handlers` keys.
 */
export enum ExportType {
  DocZip = 'docZip',
  DocList = 'docList',
  Bundle = 'bundle',
  Chronology = 'chronology',
  DocChronology = 'docChronology',
  Kim = 'kim',
  Fact = 'fact',
}

export type PaginatedResponse<T> = {
  data: T[];
  pageCount: number;
  totalCount: number;
  totalUnfilteredCount?: number;
};

export interface Option {
  label: string;
  value: string;
}

export type Environment = 'production' | 'development' | 'testing' | 'local';

export enum Relevance {
  Relevant = 'relevant',
  NotRelevant = 'notRelevant',
  MaybeRelevant = 'maybeRelevant',
}

export enum Classification {
  Primary = 'primary',
  Secondary = 'secondary',
  Future = 'future',
  Litigation = 'litigation',
}

export interface Comment {
  id: string;
  caseId: string;
  comment: string;
  commentBy: string;
  commentDate: Date;
}

export interface DateInfo {
  dateIsAmbiguous: boolean;
  dateIsAmbiguousText: string | null;
  isFutureDate: string | null;
}

export enum StyleFormat {
  Table = 'table',
  Written = 'written',
}

export enum DownloadFormat {
  DOCX = 'docx',
  EXCEL = 'xlsx',
  CSV = 'csv',
}

export enum MatterType {
  Personal = 'personal',
  General = 'general',
  Sample = 'sample',
}

/**
 * The language a matter is worked in. Drives language-aware processing and output.
 */
export enum MatterLanguage {
  English = 'en',
  French = 'fr',
  German = 'de',
  Dutch = 'nl',
  Italian = 'it',
}

export interface ApideckConnection {
  id: string;
  service_id: string;
  name: string;
  unified_api: string;
  state: ConnectionState;
  integration_state: IntegrationState;
  enabled: boolean;
  icon: string;
  logo: string;
  authorize_url: string;
}

export enum EmailTemplateOptions {
  UserAddedToMatter = 'user-added-to-matter',
  Invite = 'invite',
  TaggedInComment = 'tagged-in-comment',
  MatterApprovalRequired = 'matter-approval-required',
  MatterAdminApprovalRequired = 'matter-approval-required-admin',
  MatterProcessingComplete = 'matter-processing-complete',
  MatterProcessingCompleteUpdate = 'matter-processing-complete-update',
  SubsetProcessingComplete = 'subset-processing-complete',
  SubsetProcessingCompleteUpdate = 'subset-processing-complete-update',
  MatterAccessRequested = 'matter-access-requested',
  MatterTimeEstimate = 'matter-time-estimate',
  ExportComplete = 'export-complete',
  UsageLimitExceeded = 'usage-limit-exceeded',
  KimAnswerFinished = 'kim-answer-finished',
  RunApprovalReminder = 'run-approval-reminder',
  RunAdminApprovalReminder = 'run-admin-approval-reminder',
}

export interface UsageMetrics {
  chronologies: number;
  runs: number;
  pages: number;
  facts: number;
  kimThreads: number;
  kimMessages: number;
}

export interface UsageMetricsWithTokens extends UsageMetrics {
  tokens: number;
}

export interface BillingPeriodCreditUsage {
  used: number;
  maxCredits?: number;
  // Billing-cycle dates from the active plan. Absent when the org has no billing period
  // configured (in which case `used` / `monthlyUsage` still reflect the selected window).
  startDate?: Date;
  endDate?: Date;
  isUsingTrialPackage: boolean;
  // Credits used per calendar month across the selected window (UTC, YYYY-MM keys).
  monthlyUsage: Array<{ month: string; credits: number }>;
}

export interface SeatBasedUsagePeriod {
  periodStart: string;
  periodEnd: string;
  dailyCumulative: Array<{ date: string; cumCredits: number }>;
  totalCredits: number;
}

export interface SeatBasedUsageResponse {
  creditLimit: number | null;
  periods: SeatBasedUsagePeriod[];
}

export interface TableUsageData {
  [id: string]: UsageMetricsWithTokens & {
    matterName?: string;
    matterCode?: string;
    userEmail?: string;
    userDisabled?: boolean;
    matterType?: MatterType;
    matterCreatedBy?: string;
  };
}

export interface UsageData {
  id: string;
  organisationId: string;
  userId: string;
  matterId: string;
  date: Date;
  data: UsageMetrics;
}

export enum ChecklistStep {
  CreateMatter = 'create-matter',
  UploadDocuments = 'upload-documents',
  CreateCase = 'create-case',
  UseKim = 'use-kim',
}

export enum RunStatus {
  InQueue = 'inQueue',
  Parsing = 'parsing',
  AwaitingApproval = 'awaitingApproval',
  Extracting = 'extracting',
  Clustering = 'clustering',
  IdentifyingRelevance = 'identifyingRelevance',
  IdentifyingInconsistencies = 'identifyingInconsistencies',
  Summarising = 'summarising',
  Complete = 'complete',
  Failed = 'failed',
  Cancelled = 'cancelled',
  ApprovalTimeout = 'approvalTimeout',
}

export interface DurationEstimate {
  minutes?: number;
  hours?: number;
  days?: number;
}

export enum ForbiddenStatuses {
  ForbiddenNotInOrg = 'ForbiddenNotInOrg',
  ForbiddenNoAccess = 'ForbiddenNoAccess',
}

export enum Fonts {
  TimesNewRoman = 'Times New Roman',
  Arial = 'Arial',
}

export enum TextAlignment {
  Left = 'left',
  Both = 'both',
}

export interface DocumentDownloadOptions {
  fontFamily: Fonts;
  textAlignment: TextAlignment;
  includeReferences: boolean;
}

export enum PersonType {
  Person = 'person',
  Organisation = 'organisation',
  Place = 'place',
}

export enum Jurisdiction {
  EnglandAndWales = 'England & Wales',
  USFederal = 'US Federal',
}

export type ReferencesDict = Record<string, { id: string; type: 'fact' | 'chunk' }>;
export type NewKimReferencesDict = Record<string, { id: string; type: ReferenceType }>;

/**
 * Matter-party entities (dramatis personae) persist their references wrapped alongside the
 * structured party context, while all other entities persist a flat {@link ReferencesDict}.
 * The wrapping is produced by chronos-ml's `_build_references_payload` (WEX-1311). Consumers
 * should run the stored value through {@link normalisePersonReferences} to obtain the flat dict.
 */
export type MatterPartyReferences = {
  retrieved_references: ReferencesDict | null;
  matter_party_context?: unknown;
};

/** The two shapes the `person.references` column can hold. */
export type PersonReferences = ReferencesDict | MatterPartyReferences;

/** Normalise either {@link PersonReferences} shape to the flat {@link ReferencesDict} the app consumes. */
export function normalisePersonReferences(
  references: PersonReferences | null | undefined,
): ReferencesDict | null {
  if (!references) {
    return null;
  }
  if ('retrieved_references' in references) {
    return (references as MatterPartyReferences).retrieved_references ?? null;
  }
  return references as ReferencesDict;
}

export enum DocProcessingErrorType {
  FactExtractError = 'factExtractError',
  FileError = 'fileError',
  TextractError = 'textractError',
}

export type DocumentDownloadType = 'original' | 'highlighted' | 'asPDF';

/** Subset of `DocumentDownloadType` exposed by the docs-zip variant picker. */
export type ZipOption = Exclude<DocumentDownloadType, 'highlighted'>;

export enum UploadFailureReason {
  Duplicate = 'duplicate',
  UnsupportedFileFormat = 'unsupportedFileFormat',
  NoFileAvailable = 'noFileAvailable',
  DownloadFailed = 'downloadFailed',
  StorageError = 'storageError',
  EncryptedFile = 'encryptedFile',
  InvalidArchive = 'invalidArchive',
  UnsafeFilePath = 'unsafeFilePath',
  FileTooLarge = 'fileTooLarge',
  TooManyEntries = 'tooManyEntries',
  UploadError = 'uploadError',
}

export type OrganisationWithCounts = OrganisationGetOneResponseDto & {
  userCount: number;
  matterCount: number;
};

export interface Interval {
  days?: number | undefined;
  hours?: number | undefined;
  minutes?: number | undefined;
}

export enum DisputeStatus {
  PreAction = 'Pre-action',
  Claim = 'Claim',
  Trial = 'Trial',
  PostTrial = 'Post-trial',
}

// Once 2.0 migration complete, the deprecated fields from FactV2 can be dropped, and we can just merge the two entities
export type SubsetFact = Omit<FactV2, 'isKey' | 'relevance' | 'significance'> &
  Pick<FactSubsetRelevance, 'isKey' | 'relevance' | 'significance'>;

export interface MatterDocsFilters {
  processed?: boolean;
  extensions?: string[];
  startDate?: string;
  endDate?: string;
  documentTypes?: string[];
  documentIssues?: string[];
  hasKeyFacts?: boolean;
  hasRelevantFacts?: boolean;
  searchQuery?: string;
  personNames?: string[];
  runIds?: string[];
  personIds?: string[];
  author?: string;
  metadataFilters?: MetadataFieldFilter[];
  docIds?: string[];
  includeDeduplicatedDocs?: boolean;
  folderId?: string;
  /**
   * When true and `folderId` is set, also include docs whose files were
   * uploaded to this folder (or any descendant) but were rejected as
   * duplicates of an existing file (resolved via upload_failure.duplicateFileId).
   */
  includeFolderDeduplicatedDocs?: boolean;
  isPleading?: boolean;
}

export type RunsFilter = Array<{
  id: string;
  createdDate: Date;
  name: string | null;
}>;
export type PersonsFilter = Array<{
  id: string;
  name: string;
}>;
export type IssuesFilter = Array<{
  id: string;
  label: string;
}>;

export type FoldersFilter = Array<{
  id: string;
  path: string;
}>;

export interface MatterDocFilterOptions {
  types: string[];
  extensions: string[];
  issues: string[];
  persons: PersonsFilter;
  runs: RunsFilter;
  folders: FoldersFilter;
}

export interface SummaryContent {
  name: string;
  content: string;
  subquestions: {
    question: string;
    answer: string;
  }[];
}

// Kim Message Types
export enum KimMessageType {
  User = 'user',
  Ai = 'ai',
  AiReasoning = 'ai-reasoning',
  AiSearch = 'ai-search',
  Compacting = 'compacting',
}

export type KimMessagesStreamType = KimMessageType | 'composing';

export enum ArtifactType {
  Draft = 'draft',
  Extraction = 'extraction',
  Chronology = 'chronology',
}

export enum KimMessageActionType {
  CreateFactChronology = 'createFactChronology',
  AddToFactChronology = 'addToFactChronology',
  CreateDocChronology = 'createDocChronology',
  AddToDocChronology = 'addToDocChronology',
}

export enum ReferenceType {
  Fact = 'fact',
  Chunk = 'chunk',
  Entity = 'entity',
  Document = 'doc',
  ThreadFileChunk = 'thread_file_chunk',
  ThreadFile = 'thread_file',
}

export interface TextBlock {
  type: 'text';
  value: string;
}

export interface ReferenceBlock {
  type: 'reference';
  id: string;
  refType: ReferenceType;
}

export interface ArtifactBlock {
  type: 'artifact';
  id: string;
  name: string;
  artifactType: ArtifactType;
  content: ArtifactContent[];
  versionNumber: number;
}

export interface SurveyQuestion {
  id: string;
  title: string;
  question: string;
  multiSelect: boolean;
  choices: {
    text: string;
    preSelected: boolean;
  }[];
}

export interface SurveyBlock {
  type: 'survey';
  id: string;
  questions: SurveyQuestion[];
}

export type ArtifactContent = TextBlock | ReferenceBlock;

export interface InconsistencyReference {
  type: 'inconsistency_reference';
  // 'Claim' | 'Evidence'.
  name: string;
  reference: ReferenceBlock;
}

export interface InconsistencyBlock {
  type: 'inconsistency';
  id: string;
  // Short heading for this inconsistency. Optional because blocks persisted
  // before the title existed in chronos-ml lack it.
  title?: string;
  description: (TextBlock | ReferenceBlock)[];
  references: InconsistencyReference[];
}

export enum AnchorDocType {
  MatterFile = 'matter_file',
  ThreadFile = 'thread_file',
}

export interface DocumentInconsistenciesBlock {
  type: 'document_inconsistencies';
  id: string;
  anchorDocType: AnchorDocType;
  anchorDocId: string;
  // One-sentence summary of the whole set. Only present on blocks persisted
  // while chronos-ml#3067 carried the title at block level, before it moved
  // onto each inconsistency.
  title?: string;
  content: InconsistencyBlock[];
}

export type ContentBlock =
  | TextBlock
  | ReferenceBlock
  | ArtifactBlock
  | SurveyBlock
  | InconsistencyBlock
  | DocumentInconsistenciesBlock;

export interface StartBlock {
  type: 'start';
}

export interface EndBlock {
  type: 'end';
}

export type StreamBlock = ContentBlock | StartBlock | EndBlock;

export const UPLOAD_PROCESSING_STATUSES = [
  'processing',
  'completed',
  'cancelled',
  'failed',
] as const;
export type UploadProcessingStatus = (typeof UPLOAD_PROCESSING_STATUSES)[number];

export const TERMINAL_UPLOAD_STATUSES = new Set<UploadProcessingStatus>([
  'completed',
  'failed',
  'cancelled',
]);

export enum FileDeletionType {
  Staged = 'staged',
  Submitted = 'submitted',
}

export enum DeletionStatus {
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

export const DeletionFailureReason = {
  /** DB transaction or connection failure mid-delete. */
  DatabaseError: 'databaseError',
  /** S3 cleanup failed; DB rows may already be gone. */
  StorageError: 'storageError',
  /** Worker died or job lost; reclaimed at next enqueue or by stalled-job handler. */
  Stalled: 'stalled',
  /** Catch-all for unrecognised errors. */
  Unknown: 'unknown',
} as const;

export type DeletionFailureReason =
  (typeof DeletionFailureReason)[keyof typeof DeletionFailureReason];

export enum PricingModel {
  Usage = 'usage',
  SeatBased = 'seat_based',
}

export enum BlockedReason {
  FairUsageStreak = 'fair_usage_streak',
  ShadowLimitExceeded = 'shadow_limit_exceeded',
  OrgLimitExceeded = 'org_limit_exceeded',
}
